import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, IconButton, Collapse, Grid, Skeleton } from '@mui/material';
import { AutoAwesome, ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material';
import StatusPill, { PillKind } from './StatusPill';
import ApiContext from 'contexts/ApiContext';

interface AiIssue {
  stepUid?: string;
  stepTitle: string;
  configName: string;
  result: 'true' | 'false' | 'other';
  message?: string;
}

interface Props {
  history: any[];
  /** Called with stepUid when a row is clicked. */
  onIssueClick?: (stepUid: string) => void;
  /** Summary tier still in flight — show the real header with a skeleton
   * body instead of hiding the card (there may turn out to be checks once
   * it resolves, we just don't know yet). */
  loading?: boolean;
  /** Needed to check whether the team has any AI detection configured at
   * all — teams with none should never see this card, not even its loader. */
  teamUid?: string;
}

const RESULT_COPY: Record<'true' | 'false' | 'other', { label: string; kind: PillKind }> = {
  true: { label: 'Match', kind: 'success' },
  false: { label: 'No Match', kind: 'error' },
  other: { label: 'Inconclusive', kind: 'warning' }
};

/**
 * Aggregates every AI detection result across the transaction history into
 * a 2-column scannable list inside the card body. Same data the per-step
 * AI Detection (N) sub-panels render — different aggregation level. Click
 * a row → smooth-scroll to the corresponding step card (id="step-{uid}").
 *
 * Header sits on a tinted background (info.light) matching the Crane mock.
 * The card is hidden entirely when no detection results exist on the txn.
 */
const AiIssuesSummary: React.FC<Props> = ({ history, onIssueClick, loading = false, teamUid }) => {
  const apiContext = useContext(ApiContext);
  const [expanded, setExpanded] = useState(true);
  // null = haven't checked yet, true/false = team does/doesn't have any AI
  // detection configs at all. Teams with none never show this card — not
  // even its loading skeleton, since we already know there's nothing to
  // summarize regardless of what the transaction's own history looks like.
  const [detectionConfigured, setDetectionConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    if (!apiContext || !teamUid) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await (apiContext as any).getDetectionConfig?.(teamUid);
        const configs = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setDetectionConfigured(configs.length > 0);
      } catch {
        if (!cancelled) setDetectionConfigured(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiContext, teamUid]);

  const issues = useMemo<AiIssue[]>(() => {
    if (!Array.isArray(history)) return [];
    const out: AiIssue[] = [];
    for (const event of history) {
      const detections = event?.detectionResults;
      if (!Array.isArray(detections)) continue;
      const stepTitle = event?.step?.title || event?.stepName || 'Unknown step';
      const stepUid = event?.step?.uid || '';
      for (const det of detections) {
        if (!det || det.sourcesResolved === false) continue;
        const rawResult = (det.type || 'other').toString();
        const result: 'true' | 'false' | 'other' =
          rawResult === 'true' || rawResult === 'false' ? rawResult : 'other';
        out.push({
          stepUid,
          stepTitle,
          configName: det.name || det.configName || 'AI check',
          result,
          message: det.aiMessage
        });
      }
    }
    return out;
  }, [history]);

  // Not configured, or still checking whether it is — stay invisible rather
  // than showing a skeleton (or an empty-state message) for a team that has
  // no AI detection at all. Once we know it IS configured, though, the card
  // always shows — either real results, a loading skeleton, or an explicit
  // "no AI summary" message, never a silent disappearance.
  if (!detectionConfigured) return null;

  const flagged = issues.filter((i) => i.result !== 'true');
  const headerPill = loading ? null : issues.length === 0 ? (
      <StatusPill kind="neutral" label="No checks yet" />
    ) : flagged.length === 0 ? (
      <StatusPill kind="success" label={`${issues.length} checks passed`} />
    ) : (
      <StatusPill kind="warning" label={`${flagged.length} issue${flagged.length === 1 ? '' : 's'} detected`} />
    );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden'
      }}
    >
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          bgcolor: 'primary.dark',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ color: '#fff', fontSize: 18 }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#fff' }}
          >
            AI and Verification Summary
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {headerPill}
          <IconButton size="small" sx={{ color: '#fff' }}>
            <MoreVert fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: '#fff' }}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ p: 2, minHeight: 140 }}>
            <Grid container spacing={1.5}>
              {[0, 1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : issues.length === 0 ? (
          <Box
            sx={{
              p: 3,
              minHeight: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No AI verification checks have run on this transaction yet.
            </Typography>
          </Box>
        ) : (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={1.5}>
            {issues.map((iss, idx) => {
              const copy = RESULT_COPY[iss.result];
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={idx}
                  onClick={() => iss.stepUid && onIssueClick?.(iss.stepUid)}
                  sx={{
                    cursor: iss.stepUid && onIssueClick ? 'pointer' : 'default'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      '&:hover':
                        iss.stepUid && onIssueClick ? { bgcolor: 'grey.50' } : undefined
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.3 }}
                      >
                        {iss.configName}{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          ({iss.stepTitle})
                        </Typography>
                      </Typography>
                    </Box>
                    <StatusPill kind={copy.kind} label={copy.label} />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default AiIssuesSummary;
